import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Device, Playlist, Schedule, ScheduleAssignment } from '@cornerstone/shared';
import { api, type Member } from '../../client/api';
import { PageContainer } from '../../nav/PageContainer';
import { PageTitle } from '../../nav/PageTitle';
import { toast } from '../../components/toast';
import { cn } from '../../utils/cn';
import { API_BASE } from '../../client/config';
import { Button, Panel } from '../../components/ui';
import { ContentSkeleton } from '../../components/skeleton/ContentSkeleton';
import { contentPreviewUrl } from '../../client/previewUrl';
import { ApprovalChangeReview, type ApprovalChangeReviewData } from '../../components/ApprovalChangeReview';
import { TimetableApprovalReview } from '../../components/TimetableApprovalReview';
import type { DeviceGroup, GroupActor, GroupDevice } from '../../store/deviceGroups';

// =============================================================================
// 콘텐츠 승인관리 (Station C 제안 p29 ④ 검토·승인).
//
// 승인 단위는 송출 스케줄. 상태는 서버 컬럼 두 개로 파생한다:
//   published=false, reviewNote=null → 승인대기
//   published=false, reviewNote=…    → 반려(보완요청)
//   published=true                   → 승인(송출 허용)
// 승인/반려는 서버에서 content:approve(Owner/Admin)로 강제되고 감사 이력에 남는다.
// =============================================================================

type Status = 'pending' | 'approved' | 'rejected';
const statusOf = (s: Schedule): Status => (s.published ? 'approved' : s.reviewNote ? 'rejected' : 'pending');
const LABEL: Record<Status, string> = { pending: '승인대기', approved: '승인', rejected: '보완요청' };
const BADGE: Record<Status, string> = {
  pending: 'bg-amber-400/20 text-amber-600',
  approved: 'bg-green-500/15 text-green-600',
  rejected: 'bg-red/15 text-red',
};

function describeRecurrence(s: Schedule): string {
  if (s.delivery) return s.delivery.termination.kind === 'cycles'
    ? `승인 후 즉시 · ${s.delivery.termination.count}회 재생 후 복귀`
    : `승인 후 즉시 · ${new Date(s.delivery.termination.at).toLocaleString('ko-KR', { timeZone: 'Asia/Seoul', hourCycle: 'h23' })}까지 반복`;
  const r = s.recurrence;
  const hm = (h?: number, m?: number) => `${String(h ?? 0).padStart(2, '0')}:${String(m ?? 0).padStart(2, '0')}`;
  if (r.kind === 'once') return `1회 · ${r.at.slice(0, 16).replace('T', ' ')}`;
  const days = r.weekdays?.map((d) => '일월화수목금토'[d]).join('') ?? '매일';
  const range = s.validFrom || s.validUntil ? ` · ${s.validFrom ?? ''}~${s.validUntil ?? ''}` : '';
  return `${days} ${hm(r.hour, r.minute)}~${hm(r.endHour, r.endMinute)}${range}`;
}

function LegacyContentApproval() {
  const [items, setItems] = useState<Schedule[]>([]);
  const [playlists, setPlaylists] = useState<Playlist[]>([]);
  const [devices, setDevices] = useState<Device[]>([]);
  const [myRole, setMyRole] = useState<Member['role'] | 'h9' | null>(null);
  const [filter, setFilter] = useState<'all' | Status>('pending');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [initialError, setInitialError] = useState<string | null>(null);

  async function load() {
    const [s, p, d] = await Promise.all([api.listSchedules(), api.listPlaylists(), api.listDevices()]);
    setItems(s.filter(item => !item.assignmentRule));
    setPlaylists(p);
    setDevices(d);
  }
  useEffect(() => {
    void load().catch((e) => {
      const message = e instanceof Error ? e.message : String(e);
      setInitialError(message); toast(message, 'error');
    }).finally(() => setInitialLoading(false));
    // 내 조직 역할 — /me(관리자 등급)만으론 Editor/Viewer를 구분할 수 없어 멤버 목록에서 찾는다.
    void (async () => {
      try {
        const me = await api.auth.me();
        if (me.adminRole === 'h9_super') return setMyRole('h9');
        const members = await api.listMembers();
        setMyRole(members.find((m) => m.email === me.email)?.role ?? null);
      } catch {
        setMyRole(null);
      }
    })();
  }, []);

  const canApprove = myRole === 'h9' || myRole === 'Owner' || myRole === 'Admin';
  const counts = useMemo(() => {
    const c: Record<Status, number> = { pending: 0, approved: 0, rejected: 0 };
    for (const s of items) c[statusOf(s)] += 1;
    return c;
  }, [items]);

  const list = useMemo(
    () => items.filter((s) => filter === 'all' || statusOf(s) === filter).sort((a, b) => b.updatedAt.localeCompare(a.updatedAt)),
    [items, filter],
  );
  const selected = items.find((s) => s.id === selectedId) ?? list[0] ?? null;

  const targetLabel = (s: Schedule) => {
    const t = s.target;
    if (t.kind === 'playlist') return `플레이리스트 · ${playlists.find((p) => p.id === t.playlistId)?.name ?? t.playlistId}`;
    if (t.kind === 'media') return `미디어 · ${t.mediaId}`;
    if (t.kind === 'notice') return `공지 · ${t.noticeId}`;
    return '송출 중지';
  };
  const deviceLabel = (s: Schedule) => {
    if (!s.targetDeviceIds) return `전체 디바이스 ${devices.length}대`;
    const names = s.targetDeviceIds.map((id) => devices.find((d) => d.id === id)?.name ?? id);
    return `${names.length}대 · ${names.slice(0, 3).join(', ')}${names.length > 3 ? ' …' : ''}`;
  };

  async function approve() {
    if (!selected) return;
    setBusy(true);
    try {
      await api.publishSchedule(selected.id);
      toast(`'${selected.name}' 승인 — 송출이 허용되었습니다.`, 'success');
      await load();
    } catch (e) {
      toast(e instanceof Error ? e.message : String(e), 'error');
    } finally {
      setBusy(false);
    }
  }
  async function reject() {
    if (!selected) return;
    if (!note.trim()) return toast('반려 사유를 입력하세요.', 'error');
    setBusy(true);
    try {
      await api.rejectSchedule(selected.id, note.trim());
      toast(`'${selected.name}' 보완 요청 등록`, 'info');
      setNote('');
      await load();
    } catch (e) {
      toast(e instanceof Error ? e.message : String(e), 'error');
    } finally {
      setBusy(false);
    }
  }

  return (
    <PageContainer variant="full" className="flex flex-col overflow-hidden bg-bg">
      <div className="flex-none px-6 pt-6">
        <PageTitle title="기존 요청 검토·이력" actions={(['pending', 'approved', 'rejected'] as Status[]).map((k) => (
          <div key={k} className="rounded-lg border border-line bg-panel-2 px-4 py-1.5 text-center">
            <div className="text-[11px] text-text-dimmer">{LABEL[k]}</div>
            <div className={cn('text-[16px] font-bold', k === 'pending' ? 'text-amber-500' : k === 'approved' ? 'text-green-600' : 'text-red')}>{counts[k]}건</div>
          </div>
        ))} />
      </div>

      {initialLoading && <div className="min-h-0 flex-1 overflow-auto px-6 pb-6"><ContentSkeleton label="승인 요청을 불러오는 중…" variant="split" /></div>}
      {initialError && <p role="alert" className="px-6 pb-6 text-red">승인 요청을 불러오지 못했습니다. {initialError}</p>}
      <div className={initialLoading || initialError ? 'hidden' : 'grid flex-1 min-h-0 grid-cols-[1.3fr_1fr] gap-4 overflow-hidden px-6 pb-6'}>
        <section className="flex min-h-0 flex-col rounded-xl border border-line bg-panel">
          <div className="flex items-center gap-1 border-b border-line px-4 py-3">
            <h2 className="mr-3 text-[15px] font-bold text-text">승인 요청 목록</h2>
            {(['pending', 'approved', 'rejected', 'all'] as const).map((k) => (
              <button key={k} type="button" onClick={() => setFilter(k)}
                className={cn('rounded-md px-3 py-1 text-[12px] font-semibold', filter === k ? 'bg-accent text-white' : 'text-text-dim hover:bg-panel-2')}>
                {k === 'all' ? '전체' : LABEL[k]}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-[1fr_140px_120px_90px] gap-2 border-b border-line px-4 py-2 text-[11px] font-semibold text-text-dimmer">
            <span>송출명 · 콘텐츠</span><span>송출 대상</span><span>등록일시</span><span>상태</span>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto">
            {list.map((s) => (
              <button key={s.id} type="button" onClick={() => setSelectedId(s.id)}
                className={cn('grid w-full grid-cols-[1fr_140px_120px_90px] items-center gap-2 border-b border-line px-4 py-2.5 text-left text-[13px] hover:bg-panel-2',
                  selected?.id === s.id && 'bg-accent-soft/50')}>
                <div className="min-w-0">
                  <div className="truncate font-semibold text-text">{s.name}</div>
                  <div className="truncate text-[11px] text-text-dimmer">{targetLabel(s)}</div>
                </div>
                <span className="truncate text-[12px] text-text-dim">{s.targetDeviceIds ? `${s.targetDeviceIds.length}대` : '전체'}</span>
                <span className="text-[12px] text-text-dim">{s.updatedAt.slice(5, 16).replace('T', ' ')}</span>
                <span className={cn('w-fit rounded-full px-2 py-0.5 text-[11px] font-semibold', BADGE[statusOf(s)])}>{LABEL[statusOf(s)]}</span>
              </button>
            ))}
            {list.length === 0 && <p className="px-4 py-10 text-center text-[13px] text-text-dimmer">해당 상태의 요청이 없습니다.</p>}
          </div>
        </section>

        <section className="flex min-h-0 flex-col rounded-xl border border-line bg-panel">
          <div className="flex items-center justify-between border-b border-line px-4 py-3">
            <h2 className="text-[15px] font-bold text-text">승인 요청 상세</h2>
            {selected && <span className={cn('rounded-full px-2.5 py-0.5 text-[11px] font-semibold', BADGE[statusOf(selected)])}>{LABEL[statusOf(selected)]}</span>}
          </div>
          {selected ? (
            <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4">
              <div className="rounded-lg bg-[#16295C] p-5 text-white">
                <div className="text-[11px] font-semibold uppercase tracking-wider text-white/60">Preview</div>
                <div className="mt-2 text-[20px] font-bold">{selected.name}</div>
                <div className="mt-1 text-[13px] text-white/75">{targetLabel(selected)}</div>
              </div>
              <dl className="grid grid-cols-2 gap-3 text-[13px]">
                {[['송출 대상', deviceLabel(selected)], ['송출 일정', describeRecurrence(selected)], ['우선순위', String(selected.priority)], ['등록일시', selected.createdAt.slice(0, 16).replace('T', ' ')]].map(([k, v]) => (
                  <div key={k} className="rounded-md border border-line bg-panel-2 px-3 py-2">
                    <dt className="text-[11px] text-text-dimmer">{k}</dt>
                    <dd className="mt-0.5 font-medium text-text">{v}</dd>
                  </div>
                ))}
              </dl>
              {selected.reviewNote && (
                <div className="rounded-md border border-red/40 bg-red/10 px-3 py-2 text-[13px]">
                  <div className="text-[11px] font-semibold text-red">반려 사유</div>
                  <div className="mt-0.5 text-text">{selected.reviewNote}</div>
                </div>
              )}
              <div className="mt-auto">
                {canApprove ? (
                  <>
                    <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} placeholder="검토 의견 / 반려 사유"
                      className="w-full rounded-md border border-line bg-panel-2 px-3 py-2 text-[13px] text-text placeholder:text-text-dimmer focus:border-accent focus:outline-none" />
                    <div className="mt-2 flex gap-2">
                      <button type="button" disabled={busy || selected.published} onClick={() => void approve()}
                        className="flex-1 rounded-md bg-accent px-4 py-2.5 text-[13px] font-semibold text-white hover:bg-accent-hover disabled:opacity-40">승인 · 송출 허용</button>
                      <button type="button" disabled={busy} onClick={() => void reject()}
                        className="flex-1 rounded-md border border-red/50 bg-red/10 px-4 py-2.5 text-[13px] font-semibold text-red hover:bg-red/20 disabled:opacity-40">보완 요청 · 반려</button>
                    </div>
                  </>
                ) : (
                  <p className="rounded-md border border-line bg-panel-2 px-3 py-2 text-[12px] text-text-dimmer">
                    승인·반려는 기관 관리자(Admin) 이상만 수행할 수 있습니다. 현재 권한: {myRole ?? '확인 중'}
                  </p>
                )}
              </div>
            </div>
          ) : (
            <p className="px-4 py-10 text-center text-[13px] text-text-dimmer">목록에서 요청을 선택하세요.</p>
          )}
        </section>
      </div>
    </PageContainer>
  );
}

export type WorkflowRequest = {
  id: string; parentId: string; groupId: string | null;
  kind: 'content' | 'schedule' | 'timeline' | 'schedule-type';
  name: string; payload: Record<string, unknown>; contentVersionId: string | null;
  contentKind?: 'media' | 'notice' | 'playlist' | null;
  approvalMode: 'central' | 'group' | 'auto'; policyVersion: number; submissionVersion: number;
  status: 'pending' | 'approved' | 'rejected' | 'cancelled';
  decisionVia: string | null; submittedAt: string; reviewNote: string | null; canApprove: boolean;
  canCancel?: boolean; changeReview?: ApprovalChangeReviewData;
};
export type WorkflowScope = { actor: GroupActor; groups: DeviceGroup[]; devices: GroupDevice[]; orgName?: string };
export const WORKFLOW_MODES = { central: '중앙 승인', group: '그룹 승인', auto: '자동 승인' };
/**
 * 편성 승인·반려를 처리할 수 있는가 — 서버(scheduleAssignments.canDecide)와 같은 규칙.
 * 중앙 관리자는 항상, '그룹 승인' 그룹에서는 그 그룹의 그룹 관리자도 처리한다.
 */
export function canDecideAssignment(scope: WorkflowScope, assignment: { groupId: string | null }): boolean {
  if (scope.actor.central) return true;
  const group = assignment.groupId ? scope.groups.find(g => g.id === assignment.groupId) : undefined;
  if (group?.approvalMode !== 'group') return false;
  return scope.actor.groups.some(g => g.groupId === assignment.groupId && g.role === 'Admin');
}
/**
 * 이 대기 요청을 올린 사람인가 — 승인 요청 취소는 요청자만 한다(SCDS-87·98).
 * 승인자에게는 취소 버튼을 보이지 않는다. 승인자는 승인·반려로 처리한다. 서버(scheduleAssignments.remove)와 같은 규칙.
 */
export function isOwnPendingRequest(scope: WorkflowScope, assignment: Pick<ScheduleAssignment, 'approval'>): boolean {
  return assignment.approval?.status === 'pending' && !!scope.actor.userId && assignment.approval.requestedBy === scope.actor.userId;
}
// 요청을 올릴 때 서버가 적어 둔 승인 안내(scheduleAssignments.approvalReason)의 앞 문장.
const GROUP_APPROVAL_NOTE = '그룹 승인 그룹 — 그룹 관리자 승인 후 적용됩니다.';
const CENTRAL_APPROVAL_NOTE = '중앙 승인 그룹 — 소유자(중앙 관리자) 승인 후 적용됩니다.';
/**
 * 대기 중인 요청의 승인 안내를 지금 그룹 승인 방식 기준으로 다시 쓴다(SCDS-84). 요청 뒤에 그룹이
 * '그룹 승인'에서 '중앙 승인'으로 바뀌면 결재는 이미 바뀐 규칙(canDecideAssignment)을 따르는데,
 * 요청 때 적어 둔 문장은 "그룹 관리자 승인"으로 남아 있었다. 처리가 끝난 요청은 기록이라 그대로 둔다.
 */
export function currentApprovalReason(scope: WorkflowScope, assignment: { groupId: string | null;
  approval?: { status: string; reason?: string | undefined } | undefined }): string {
  const reason = assignment.approval?.reason ?? '';
  if (assignment.approval?.status !== 'pending') return reason;
  const was = [GROUP_APPROVAL_NOTE, CENTRAL_APPROVAL_NOTE].find(note => reason.includes(note));
  if (!was) return reason;
  const group = assignment.groupId ? scope.groups.find(g => g.id === assignment.groupId) : undefined;
  // 결재 규칙과 같다 — '그룹 승인'이 아니면(중앙·자동으로 바뀐 경우 포함) 중앙 관리자가 처리한다.
  const now = group?.approvalMode === 'group' ? GROUP_APPROVAL_NOTE : CENTRAL_APPROVAL_NOTE;
  return was === now ? reason : reason.replace(was, now + ' (요청 후 그룹 승인 방식이 바뀌어 현재 기준으로 표시)');
}
const WORKFLOW_KINDS = { content: '콘텐츠', schedule: '송출', timeline: '타임라인', 'schedule-type': '스케줄 타입' };
const WORKFLOW_STATUS = { pending: '승인 대기', approved: '승인됨', rejected: '반려', cancelled: '취소됨' };

export async function workflowJson<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${API_BASE}/api/${path}`, { credentials: 'include', ...init });
  if (!response.ok) {
    const body = await response.json().catch(() => null) as { error?: string } | null;
    throw new Error(body?.error || `요청 실패 (HTTP ${response.status})`);
  }
  return response.status === 204 ? undefined as T : response.json() as Promise<T>;
}

export function useWorkflowScope() {
  const [scope, setScope] = useState<WorkflowScope | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [revision, setRevision] = useState(0);
  const [pending, setPending] = useState(false);
  const pendingRef = useRef(false);
  const queued = useRef(false);
  const reload = useCallback(() => {
    if (pendingRef.current) { queued.current = true; return; }
    setRevision((value) => value + 1);
  }, []);
  useEffect(() => {
    const controller = new AbortController();
    setLoading(true); setError(null);
    void workflowJson<WorkflowScope>('device-groups', { signal: controller.signal })
      .then((data) => { if (!controller.signal.aborted) setScope(data); })
      .catch((reason: unknown) => { if (!controller.signal.aborted) { setScope(null); setError(reason instanceof Error ? reason.message : String(reason)); } })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [revision]);
  useEffect(() => {
    window.addEventListener('bricklit:scope-changed', reload);
    return () => window.removeEventListener('bricklit:scope-changed', reload);
  }, [reload]);
  const begin = () => {
    if (pendingRef.current || loading || error) return false;
    pendingRef.current = true; setPending(true); return true;
  };
  const finish = () => {
    pendingRef.current = false; setPending(false);
    if (queued.current) { queued.current = false; reload(); }
  };
  return { scope, loading, error, pending, reload, begin, finish };
}

export function WorkflowBadge({ request }: { request: WorkflowRequest }): JSX.Element {
  const label = request.status === 'approved' && request.decisionVia === 'auto' ? '자동 승인됨'
    : request.status === 'pending' ? `${WORKFLOW_MODES[request.approvalMode]} 대기` : WORKFLOW_STATUS[request.status];
  return <span className={cn('inline-block rounded-full px-2 py-0.5 text-[12px] font-semibold',
    request.status === 'approved' ? BADGE.approved : request.status === 'pending' ? BADGE.pending
      : request.status === 'rejected' ? BADGE.rejected : 'bg-panel-2 text-text-dim')}>{label}</span>;
}

export function ContentApproval(): JSX.Element {
  const workflow = useWorkflowScope();
  if (!workflow.scope) return <PageContainer fluid><PageTitle title="콘텐츠 요청 검토" />
    {workflow.error ? <><p role="alert" className="text-red">{workflow.error}</p><Button disabled={workflow.loading} onClick={workflow.reload}>다시 불러오기</Button></>
      : <ContentSkeleton label="권한을 확인하는 중…" variant="split" />}</PageContainer>;
  if (!workflow.scope.actor.enforced) return <LegacyContentApproval />;
  return <ScopedContentApproval key={workflow.scope.actor.orgId} workflow={workflow} scope={workflow.scope} />;
}

function ScopedContentApproval({ workflow, scope }: { workflow: ReturnType<typeof useWorkflowScope>; scope: WorkflowScope }): JSX.Element {
  const [requests, setRequests] = useState<WorkflowRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<WorkflowRequest['status'] | 'all'>('pending');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [confirmCancel, setConfirmCancel] = useState(false);
  useEffect(() => {
    const controller = new AbortController();
    setLoading(true); setLoadError(null);
    void workflowJson<{ actor: GroupActor; requests: WorkflowRequest[] }>('group-workflow', { signal: controller.signal })
      .then((data) => {
        if (controller.signal.aborted) return;
        if (data.actor.orgId !== scope.actor.orgId || !data.actor.enforced) throw new Error('운영 범위를 다시 확인해 주세요.');
        setRequests(data.requests);
      }).catch((reason: unknown) => { if (!controller.signal.aborted) { setRequests([]); setSelectedId(null); setLoadError(reason instanceof Error ? reason.message : String(reason)); } })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [scope]);
  const busy = workflow.pending || workflow.loading || loading;
  const reading = workflow.loading || loading;
  const blocked = busy || !!workflow.error || !!loadError;
  const visibleRequests = blocked ? [] : requests;
  const list = visibleRequests.filter((request) => filter === 'all' || request.status === filter)
    .sort((a, b) => b.submittedAt.localeCompare(a.submittedAt));
  const selected = list.find((request) => request.id === selectedId) ?? list[0] ?? null;
  const selectionKey = selected ? `${selected.id}:${selected.submissionVersion}` : '';
  useEffect(() => { setNote(''); setConfirmCancel(false); }, [selectionKey]);
  const groupName = (id: string | null) => id === null ? '공통' : scope.groups.find((group) => group.id === id)?.name ?? '조회 불가 그룹';

  async function decide(action: 'approve' | 'reject'): Promise<void> {
    if (blocked || !selected?.canApprove || selected.status !== 'pending') return;
    if (action === 'approve' && selected.changeReview?.liveChanged) return;
    if (action === 'reject' && !note.trim()) { setError('반려 사유를 입력하세요.'); return; }
    if (!workflow.begin()) return;
    setError(null);
    try {
      const updated = await workflowJson<WorkflowRequest>(`group-workflow/${encodeURIComponent(selected.id)}/${action}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ submissionVersion: selected.submissionVersion, ...(action === 'reject' ? { note: note.trim() } : {}) }),
      });
      setRequests((items) => items.map((item) => item.id === updated.id ? updated : item));
      setNote(''); workflow.reload();
    } catch (reason) { setError(reason instanceof Error ? reason.message : String(reason)); }
    finally { workflow.finish(); }
  }
  async function cancel(): Promise<void> {
    if (blocked || !confirmCancel || !selected?.canCancel || selected.status !== 'pending' || !workflow.begin()) return;
    setError(null);
    try {
      await workflowJson<void>(`group-workflow/${encodeURIComponent(selected.id)}/cancel`, { method: 'POST' });
      setRequests(items => items.map(item => item.id === selected.id ? { ...item, status: 'cancelled', canApprove: false, canCancel: false } : item));
    } catch (reason) { setError(reason instanceof Error ? reason.message : String(reason)); }
    finally { setConfirmCancel(false); workflow.reload(); workflow.finish(); }
  }
  const object = selected?.payload.object as { recurrence?: Schedule['recurrence']; validFrom?: string | null; validUntil?: string | null } | undefined;

  return <PageContainer fluid className="bg-bg text-text">
    <PageTitle className="mb-0" title="콘텐츠 요청 검토·이력" actions={<Button disabled={busy} onClick={workflow.reload}>새로고침</Button>} />
    {(error || loadError || workflow.error) && <p role="alert" className="rounded border border-red/50 bg-red/15 p-3 text-red">{error || loadError || workflow.error}</p>}
    {workflow.pending && <p role="status" className="text-[13px] text-text-dim">처리 중…</p>}
    <div className="grid gap-4 lg:grid-cols-[1.2fr_1fr]" aria-busy={busy}>
      <Panel title="승인 요청 목록">
        <div className="mb-3 flex flex-wrap gap-2">
          {(['pending', 'approved', 'rejected', 'cancelled', 'all'] as const).map((status) => <Button key={status}
            disabled={workflow.pending} aria-pressed={filter === status} onClick={() => setFilter(status)}>
            {status === 'all' ? '전체' : WORKFLOW_STATUS[status]} {visibleRequests.filter((request) => status === 'all' || request.status === status).length}
          </Button>)}
        </div>
        <div className="max-h-[65vh] space-y-2 overflow-auto">
          {reading && <ContentSkeleton label="승인 요청을 불러오는 중…" variant="table" />}
          {list.map((request) => <button type="button" key={request.id} disabled={workflow.pending}
            aria-pressed={selected?.id === request.id} onClick={() => setSelectedId(request.id)}
            className={cn('w-full rounded border border-line p-3 text-left disabled:opacity-50', selected?.id === request.id && 'bg-accent-soft')}>
            <div className="mb-1 flex flex-wrap items-center justify-between gap-2"><span className="font-semibold">{request.name}</span><WorkflowBadge request={request} /></div>
            <p className="text-[13px] text-text-dim">{WORKFLOW_KINDS[request.kind]} · {groupName(request.groupId)} · {WORKFLOW_MODES[request.approvalMode]}</p>
          </button>)}
          {!blocked && list.length === 0 && <p className="p-4 text-center text-text-dim">해당 상태의 요청이 없습니다.</p>}
        </div>
      </Panel>
      <Panel title="승인 요청 상세">
        {selected ? <div className="space-y-4">
          <h2 className="text-[20px] font-bold">{selected.name}</h2><WorkflowBadge request={selected} />
          {!!selected.payload['timetableBundle'] && selected.changeReview?.liveChanged && <p role="alert" className="rounded border border-red/40 bg-red/10 p-3 text-[13px]">원본 또는 대상 일과가 제출 후 변경되었습니다. 최신 편성을 확인하고 다시 제출하세요.</p>}
          {selected.payload['timetableBundle'] ? <TimetableApprovalReview value={selected.payload['timetableBundle']}
            groupName={groupName(selected.groupId)} devices={scope.devices} /> : selected.kind !== 'content' && selected.changeReview &&
            <ApprovalChangeReview kind={selected.kind} groupName={groupName(selected.groupId)} devices={scope.devices} review={selected.changeReview} />}
          {selected.contentVersionId && selected.contentKind && <div className="space-y-2">
            <p className="text-[13px] text-text-dim">제출 시 고정된 콘텐츠 버전임. 현재 운영 중인 기기에는 영향을 주지 않음.</p>
            <iframe key={selected.id} title="제출된 콘텐츠 미리보기" src={contentPreviewUrl(selected.contentKind, selected.contentVersionId)}
              className="aspect-video w-full rounded border border-line bg-black" />
            <a className="text-[13px] text-accent underline" href={contentPreviewUrl(selected.contentKind, selected.contentVersionId)}
              target="_blank" rel="noopener noreferrer">검토 버전 크게 보기</a>
          </div>}
          {/* 대체된 승인본은 정리된다(2026-10-13) — 기록은 남고 그때의 미리보기만 없다. */}
          {selected.contentVersionId && !selected.contentKind && <p className="rounded border border-line bg-panel-2 p-3 text-[13px] text-text-dim">
            이 요청의 승인본은 이후 새 승인본으로 대체되어 정리되었습니다. 요청 기록은 남아 있지만 당시 미리보기는 볼 수 없습니다.</p>}
          <dl className="grid grid-cols-2 gap-3 text-[14px]">
            {([['요청 종류', WORKFLOW_KINDS[selected.kind]], ['대상 그룹', groupName(selected.groupId)],
              ['승인 방식', WORKFLOW_MODES[selected.approvalMode]], ['등록일시', new Date(selected.submittedAt).toLocaleString('ko-KR', { timeZone: 'Asia/Seoul', hourCycle: 'h23' })],
              ...(object?.recurrence ? [['송출 일정', describeRecurrence(object as Schedule)]] : [])]).map(([label, value]) =>
              <div key={label} className="rounded border border-line bg-panel-2 p-3"><dt className="text-text-dim">{label}</dt><dd>{value}</dd></div>)}
          </dl>
          {selected.reviewNote && <p className="rounded border border-red/40 bg-red/10 p-3 text-[14px]">반려 사유: {selected.reviewNote}</p>}
          {selected.canApprove && selected.status === 'pending' ? <>
            <label className="block text-[14px]">검토 의견 / 반려 사유<textarea aria-label="반려 사유" value={note} disabled={blocked}
              onChange={(event) => setNote(event.target.value)} rows={3} className="mt-1 w-full rounded border border-line bg-panel-2 p-3 text-text" /></label>
            <div className="flex gap-2"><Button variant="primary" disabled={blocked || selected.changeReview?.liveChanged} onClick={() => void decide('approve')}>승인</Button>
              <Button variant="danger" disabled={blocked || !note.trim()} onClick={() => void decide('reject')}>반려</Button></div>
          </> : <p className="text-[13px] text-text-dim">{selected.status === 'pending' ? '이 요청을 처리할 승인 권한이 없습니다.' : '처리가 완료된 요청입니다.'}</p>}
          {selected.canCancel && selected.status === 'pending' && <div className="border-t border-line pt-3">
            {confirmCancel ? <div role="group" aria-label="승인 요청 취소 확인" className="space-y-2">
              <p className="text-[13px] text-text-dim">이 대기 요청을 취소합니다. 현재 승인된 운영 편성은 유지됩니다.</p>
              <div className="flex gap-2"><Button variant="danger" disabled={blocked} onClick={() => void cancel()}>요청 취소 확인</Button>
                <Button disabled={blocked} onClick={() => setConfirmCancel(false)}>돌아가기</Button></div>
            </div> : <Button disabled={blocked} onClick={() => setConfirmCancel(true)}>승인 요청 취소</Button>}
          </div>}
        </div> : reading ? <ContentSkeleton label="승인 요청 상세를 불러오는 중…" variant="form" />
          : <p className="text-text-dim">{loadError || workflow.error ? '요청 상세를 표시할 수 없습니다.' : workflow.pending ? '요청을 처리하는 중…' : '목록에서 요청을 선택하세요.'}</p>}
      </Panel>
    </div>
  </PageContainer>;
}
